import { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';

export function PdfRearrange() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [order, setOrder] = useState('');
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    const buf = await readFileAsArrayBuffer(f);
    const doc = await PDFDocument.load(buf);
    const total = doc.getPageCount();
    setPageCount(total);
    setOrder(Array.from({ length: total }, (_, i) => i + 1).join(', '));
  }

  async function run() {
    if (!file) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const src = await PDFDocument.load(buf);
      const indices: number[] = [];
      for (const part of order.split(',')) {
        const n = parseInt(part.trim(), 10);
        if (Number.isFinite(n) && n >= 1 && n <= pageCount) indices.push(n - 1);
      }
      if (!indices.length) {
        toast.error('Invalid page order');
        return;
      }
      const out = await PDFDocument.create();
      const copied = await out.copyPages(src, indices);
      copied.forEach((p) => out.addPage(p));
      const bytes = await out.save();
      saveAs(
        bytesToBlob(bytes),
        file.name.replace(/\.pdf$/i, '') + '-reordered.pdf',
      );
      toast.success('Saved');
    } catch {
      toast.error('Reorder failed');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-rearrange"
        title="Reorder PDF pages"
        description="Reorder, duplicate, or drop PDF pages. Provide a comma-separated list."
        badges={pageCount ? <Badge>{pageCount} pages</Badge> : null}
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>
                Change
              </Button>
            </div>
            <Field
              label="New page order"
              hint={`1 to ${pageCount}, comma-separated. Repeat to duplicate, omit to remove.`}
            >
              <Input value={order} onChange={(e) => setOrder(e.target.value)} className="font-mono" />
            </Field>
          </Card>
          <Card>
            <Button
              variant="primary"
              className="w-full"
              disabled={working}
              loading={working}
              onClick={run}
            >
              <Download size={12} />
              Save
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfRearrange;
